import {
  PaymentPlanInstalmentDTOStatusEnum,
  PaymentPlanInstalmentReschedulePreviewDTO,
  PaymentPlanInstalmentRescheduleRowDTO,
} from '@reposit/api-client';
import { startCase, toLower } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import styled from 'styled-components';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../../containers/FlashMessage';
import {
  DELETE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
  RESCHEDULE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
} from '../../redux/payment-plan/payment-plan.actions';
import { penceToPounds } from '../../utils/common.utils';
import Button from '../Button';
import FlashMessage from '../FlashMessage';
import Modal from '../Modal';
import { Header3, P2, P3 } from '../Typography';

export interface ModifiablePaymentPlanInstalment {
  id?: string;
  instalmentNumber?: number;
  dueDate: string;
  amount: number;
  status: string;
}

export const isDeletablePaymentPlanInstalmentStatus = (status: string): boolean =>
  status === PaymentPlanInstalmentDTOStatusEnum.FAILED || status === PaymentPlanInstalmentDTOStatusEnum.PENDING;

interface ModifyPaymentPlanInstalmentModalProps {
  instalment: ModifiablePaymentPlanInstalment;
  isDeleting: boolean;
  isPreviewing: boolean;
  isConfirming: boolean;
  onDismiss: () => void;
  onDelete?: () => Promise<boolean> | boolean;
  onPreviewReschedule?: (rescheduledDate: string) => Promise<PaymentPlanInstalmentReschedulePreviewDTO | undefined>;
  onConfirmReschedule?: (rescheduledDate: string, expectedCurrentScheduleHash: string) => Promise<boolean> | boolean;
}

const ItemSummary = styled.div`
  background: #fff;
  border: 1px solid ${(props) => props.theme.colors.dividerLight};
  border-radius: 8px;
  margin-bottom: 10px;
  overflow: hidden;
  padding: 22px 14px 12px;
  position: relative;
`;

const CardTypePill = styled.span<{ deletion?: boolean }>`
  background: ${(props) => (props.deletion ? '#FFF1F1' : props.theme.colors.bgHoverAccent)};
  border-bottom-right-radius: 4px;
  color: ${(props) => (props.deletion ? props.theme.colors.negative : props.theme.colors.tertiaryBrand)};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 10px;
  font-weight: 700;
  left: 0;
  letter-spacing: 0.07em;
  padding: 3px 8px;
  position: absolute;
  text-transform: uppercase;
  top: 0;
`;

const SectionDivider = styled.div<{ deletion?: boolean; created?: boolean }>`
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
  margin-top: 4px;

  &::before,
  &::after {
    background: ${(props) =>
      props.deletion ? props.theme.colors.negative : props.created ? props.theme.colors.primaryBrand : '#F5A623'};
    content: '';
    flex: 1;
    height: 1px;
    opacity: 0.4;
  }
`;

const SectionDividerText = styled.span<{ deletion?: boolean; created?: boolean }>`
  color: ${(props) =>
    props.deletion ? props.theme.colors.negative : props.created ? props.theme.colors.primaryBrand : '#B87A00'};
  flex-shrink: 0;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ItemSummaryTopRow = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const ItemSummaryLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemSummaryName = styled.span<{ deleted?: boolean }>`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
  opacity: ${(props) => (props.deleted ? 0.5 : 1)};
  text-decoration: ${(props) => (props.deleted ? 'line-through' : 'none')};
`;

const ItemSummaryAmount = styled.span<{ deleted?: boolean }>`
  align-items: center;
  color: ${(props) => props.theme.colors.body};
  display: flex;
  flex-shrink: 0;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
  gap: 6px;
  margin-left: 12px;
  opacity: ${(props) => (props.deleted ? 0.5 : 1)};
  text-decoration: ${(props) => (props.deleted ? 'line-through' : 'none')};
`;

const ItemSummarySubRow = styled.div<{ deleted?: boolean }>`
  color: ${(props) => props.theme.colors.placeholder};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 12px;
  margin-top: 4px;
  opacity: ${(props) => (props.deleted ? 0.5 : 1)};
  text-decoration: ${(props) => (props.deleted ? 'line-through' : 'none')};
`;

const PreviousValue = styled.span`
  color: ${(props) => props.theme.colors.placeholder};
  text-decoration: line-through;
`;

const ChangeArrow = styled.span`
  color: ${(props) => props.theme.colors.placeholder};
  margin: 0 6px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${(props) => props.theme.colors.dividerLight};
  margin: 20px 0;
`;

const SectionTitle = styled.p`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 4px;
`;

const SectionText = styled.p`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 13px;
  margin: 0 0 16px;
`;

const DateChangeRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const CurrentDateValue = styled.span`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
`;

const DateChangeArrow = styled.span`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 18px;
  font-weight: 700;
`;

const DateInputWrapper = styled.div`
  align-items: stretch;
  border: 1px solid ${(props) => props.theme.colors.inactive};
  border-radius: 4px;
  display: flex;
  max-width: 260px;
  overflow: hidden;
  transition: border-color 0.15s;

  &:focus-within {
    border-color: ${(props) => props.theme.colors.primaryBrand};
  }
`;

const DateInputLabel = styled.label`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const DateInput = styled.input`
  border: none;
  color: ${(props) => props.theme.colors.body};
  flex: 1;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  outline: none;
  padding: 10px 12px;
  width: 100%;

  &:disabled {
    background: ${(props) => props.theme.colors.bgAccent};
    color: ${(props) => props.theme.colors.disabled};
  }
`;

const ValidationError = styled(P3)`
  color: ${(props) => props.theme.colors.negative};
  margin-bottom: 0;
  margin-top: 6px;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 16px;

  button {
    margin: 0;
  }
`;

const FixedModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const ScrollableFormContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 10px;
`;

const FormFooter = styled.div`
  flex: 0 0 auto;
`;

const PreviewHeader = styled.div`
  flex: 0 0 auto;
`;

const PreviewInstalmentList = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 10px;
`;

const LeftAlignedAction = styled.div`
  display: flex;
  justify-content: flex-start;

  button {
    margin: 0;
  }
`;

const DisabledActionTooltip = styled.span`
  display: inline-flex;
`;

const MODAL_MAX_WIDTH = '1080px';
const MODAL_HEIGHT = '500px';

const formatDate = (date: string | Date) =>
  typeof date === 'string' ? moment(date, 'YYYY-MM-DD').format('DD-MM-YYYY') : moment(date).format('DD-MM-YYYY');
const formatDateWithSlashes = (date: string | Date) =>
  typeof date === 'string' ? moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY') : moment(date).format('DD/MM/YYYY');
const formatDateInputValue = (date: string | Date) =>
  typeof date === 'string' ? moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD') : moment(date).format('YYYY-MM-DD');
const fmt = (pence: number) => `£${penceToPounds(pence)}`;

const formatInstalmentNumber = (row: ModifiablePaymentPlanInstalment | PaymentPlanInstalmentRescheduleRowDTO) =>
  row.instalmentNumber !== undefined ? row.instalmentNumber : '-';

const formatStatus = (status: string) => startCase(toLower(status));

const formatRowSubRow = (row: ModifiablePaymentPlanInstalment | PaymentPlanInstalmentRescheduleRowDTO) =>
  [`Instalment number: ${formatInstalmentNumber(row)}`, formatStatus(row.status)].filter(Boolean).join(' · ');

const renderChangedValue = (oldValue: React.ReactNode, newValue: React.ReactNode) => (
  <>
    <PreviousValue>{oldValue}</PreviousValue>
    <ChangeArrow>→</ChangeArrow>
    {newValue}
  </>
);

const renderChangedStatus = (oldStatus: string, newStatus: string) =>
  toLower(oldStatus) === toLower(newStatus)
    ? formatStatus(newStatus)
    : renderChangedValue(formatStatus(oldStatus), formatStatus(newStatus));

const renderSummaryCard = (
  row: ModifiablePaymentPlanInstalment | PaymentPlanInstalmentRescheduleRowDTO,
  options: {
    deleted?: boolean;
    amountOverride?: React.ReactNode;
    nameOverride?: React.ReactNode;
    subRowOverride?: React.ReactNode;
  } = {},
) => (
  <ItemSummary>
    <CardTypePill deletion={options.deleted}>Instalment</CardTypePill>
    <ItemSummaryTopRow>
      <ItemSummaryLeft>
        <ItemSummaryName deleted={options.deleted}>{options.nameOverride || formatDate(row.dueDate)}</ItemSummaryName>
        <ItemSummarySubRow deleted={options.deleted}>{options.subRowOverride || formatRowSubRow(row)}</ItemSummarySubRow>
      </ItemSummaryLeft>
      <ItemSummaryAmount deleted={options.deleted}>{options.amountOverride || fmt(row.amount)}</ItemSummaryAmount>
    </ItemSummaryTopRow>
  </ItemSummary>
);

const ModifyPaymentPlanInstalmentModal: React.FC<ModifyPaymentPlanInstalmentModalProps> = ({
  instalment,
  isDeleting,
  isPreviewing,
  isConfirming,
  onDismiss,
  onDelete,
  onPreviewReschedule,
  onConfirmReschedule,
}) => {
  const today = moment().format('YYYY-MM-DD');
  const instalmentDueDate = formatDateInputValue(instalment.dueDate);
  const [rescheduledDate, setRescheduledDate] = useState(instalmentDueDate);
  const [hasSubmittedDate, setHasSubmittedDate] = useState(false);
  const [preview, setPreview] = useState<PaymentPlanInstalmentReschedulePreviewDTO | undefined>();
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] = useState(false);
  const [flashMessagePayload, dismissFlashMessage] = useFlashMessage([
    DELETE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
    RESCHEDULE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
  ]);

  const parsedRescheduledDate = moment(rescheduledDate, 'YYYY-MM-DD', true);
  const isDateValid =
    rescheduledDate !== '' && parsedRescheduledDate.isValid() && !parsedRescheduledDate.isBefore(moment().startOf('day'), 'day');
  const validationError = hasSubmittedDate && !isDateValid ? 'Choose today or a future date.' : null;
  const isFormDisabled = isDeleting || isPreviewing || isConfirming;
  const canReschedule = !!onPreviewReschedule && !!onConfirmReschedule;
  const isFailedInstalment = instalment.status === PaymentPlanInstalmentDTOStatusEnum.FAILED;
  const canDeleteInstalment = Boolean(onDelete && isDeletablePaymentPlanInstalmentStatus(instalment.status));
  const hasRescheduleDateChanged = rescheduledDate !== instalmentDueDate;
  const previewDisabledReason =
    rescheduledDate === ''
      ? 'Choose a reschedule date before previewing.'
      : !isDateValid
        ? 'Choose today or a future date before previewing.'
        : !hasRescheduleDateChanged
          ? 'Choose a different reschedule date before previewing.'
          : undefined;
  const isPreviewButtonDisabled = isFormDisabled || !!previewDisabledReason;
  const rescheduleDescription = isFailedInstalment
    ? 'Create a new pending instalment and remove the failed payment charge.'
    : 'Select a new due date for this pending instalment.';

  const previewReplacement = preview
    ? preview.proposedSchedule.find((row) => row.replacementForInstalmentId === instalment.id)
    : undefined;
  const isReplacementInstalmentNumberUnchanged = Boolean(
    previewReplacement && preview && preview.originalInstalmentNumber === preview.newInstalmentNumber,
  );
  const currentRowsById = new Map((preview ? preview.currentSchedule : []).filter((row) => row.id).map((row) => [row.id, row]));
  const proposedRowsById = new Map((preview ? preview.proposedSchedule : []).filter((row) => row.id).map((row) => [row.id, row]));

  const handlePreviewReschedule = async () => {
    setHasSubmittedDate(true);
    if (!onPreviewReschedule || !isDateValid || !hasRescheduleDateChanged) return;
    dismissFlashMessage();
    const result = await onPreviewReschedule(rescheduledDate);
    if (result) {
      setPreview(result);
    }
  };

  const handleConfirmReschedule = async () => {
    if (!preview || isConfirming || !onConfirmReschedule) return;
    await onConfirmReschedule(preview.rescheduledDueDate, preview.currentScheduleHash);
  };

  const handleDelete = async () => {
    if (isDeleting || !onDelete) return;
    await onDelete();
  };

  const handleDismiss = () => {
    dismissFlashMessage();
    onDismiss();
  };

  const handleCloseDeleteConfirmation = () => {
    dismissFlashMessage();
    setIsDeleteConfirmationVisible(false);
  };

  const handleGoBackToForm = () => {
    dismissFlashMessage();
    setPreview(undefined);
  };

  if (isDeleteConfirmationVisible && canDeleteInstalment) {
    return (
      <Modal
        onDismiss={isDeleting ? undefined : handleCloseDeleteConfirmation}
        capHeight
        height={MODAL_HEIGHT}
        maxWidth={MODAL_MAX_WIDTH}
        scrollable
      >
        {flashMessagePayload ? (
          <FlashMessage payload={flashMessagePayload} onDismiss={dismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
        ) : null}
        <Header3 style={{ marginBottom: 15 }}>Delete Payment Plan Instalment</Header3>
        <P2 style={{ marginBottom: 20 }}>
          Deleting this instalment reduces the payment plan total, but it does not reduce the underlying order balance.
          {isFailedInstalment
            ? ' The payment plan will no longer default because of this instalment.'
            : ' This instalment will no longer be collected as part of the payment plan.'}{' '}
          The payment plan user will still be expected to pay any remaining instalments. Only do this after the instalment has
          been paid by bank transfer or another form of manual payment.
        </P2>
        <P2 style={{ fontWeight: 'bold', marginBottom: 20 }}>This action will not create a manual payment itself.</P2>
        <P2 style={{ marginBottom: 20 }}>Are you sure you would like to delete this instalment?</P2>
        {renderSummaryCard(instalment)}
        <ActionBar>
          <Button buttonType="secondary" onClick={handleCloseDeleteConfirmation} disabled={isDeleting} noArrow>
            Cancel
          </Button>
          <Button buttonType="warning" onClick={handleDelete} disabled={isDeleting} noArrow>
            Confirm
          </Button>
        </ActionBar>
      </Modal>
    );
  }

  if (preview) {
    return (
      <Modal onDismiss={isFormDisabled ? undefined : handleDismiss} capHeight height={MODAL_HEIGHT} maxWidth={MODAL_MAX_WIDTH}>
        <FixedModalLayout>
          {flashMessagePayload ? (
            <FlashMessage payload={flashMessagePayload} onDismiss={dismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
          ) : null}
          <PreviewHeader>
            <Header3 style={{ marginBottom: 4 }}>Reschedule Instalment</Header3>
            <P2 style={{ marginBottom: 20 }}>Review the schedule changes below before confirming.</P2>
          </PreviewHeader>

          <PreviewInstalmentList>
            {isReplacementInstalmentNumberUnchanged && previewReplacement ? (
              <>
                <SectionDivider>
                  <SectionDividerText>Modified</SectionDividerText>
                </SectionDivider>
                {renderSummaryCard(previewReplacement, {
                  nameOverride: renderChangedValue(formatDate(preview.originalDueDate), formatDate(preview.rescheduledDueDate)),
                  subRowOverride: (
                    <>
                      {`Instalment number: ${formatInstalmentNumber(previewReplacement)}`}
                      {' · '}
                      {renderChangedStatus(preview.previousStatus, previewReplacement.status)}
                    </>
                  ),
                })}
              </>
            ) : (
              <>
                <SectionDivider deletion>
                  <SectionDividerText deletion>Deleted</SectionDividerText>
                </SectionDivider>
                {renderSummaryCard(instalment, { deleted: true })}

                {previewReplacement ? (
                  <>
                    <SectionDivider created>
                      <SectionDividerText created>Created</SectionDividerText>
                    </SectionDivider>
                    {renderSummaryCard(previewReplacement)}
                  </>
                ) : null}
              </>
            )}

            {preview.renumberedInstalments.length ? (
              <>
                {isReplacementInstalmentNumberUnchanged ? null : (
                  <SectionDivider>
                    <SectionDividerText>Modified</SectionDividerText>
                  </SectionDivider>
                )}
                {preview.renumberedInstalments.map((renumbered) => {
                  const currentRow = currentRowsById.get(renumbered.instalmentId);
                  const proposedRow = proposedRowsById.get(renumbered.instalmentId);
                  if (!currentRow || !proposedRow) return null;

                  return (
                    <React.Fragment key={renumbered.instalmentId}>
                      {renderSummaryCard(proposedRow, {
                        subRowOverride: [
                          `Instalment number: ${formatInstalmentNumber(proposedRow)}`,
                          `Renumbered from #${renumbered.originalInstalmentNumber}`,
                          formatStatus(proposedRow.status),
                        ]
                          .filter(Boolean)
                          .join(' · '),
                      })}
                    </React.Fragment>
                  );
                })}
              </>
            ) : null}
          </PreviewInstalmentList>

          <ActionBar>
            <Button buttonType="secondary" onClick={handleGoBackToForm} disabled={isFormDisabled} noArrow>
              Go back
            </Button>
            <Button onClick={handleConfirmReschedule} disabled={isFormDisabled}>
              Confirm
            </Button>
          </ActionBar>
        </FixedModalLayout>
      </Modal>
    );
  }

  return (
    <Modal onDismiss={isFormDisabled ? undefined : handleDismiss} capHeight height={MODAL_HEIGHT} maxWidth={MODAL_MAX_WIDTH}>
      <FixedModalLayout>
        <ScrollableFormContent data-testid="modify-payment-plan-instalment-scrollable-content">
          {flashMessagePayload ? (
            <FlashMessage payload={flashMessagePayload} onDismiss={dismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
          ) : null}
          <Header3 style={{ marginBottom: 16 }}>{canReschedule ? 'Modify Instalment' : 'Delete Instalment'}</Header3>

          {renderSummaryCard(instalment)}

          {canReschedule ? (
            <>
              <Divider />

              <SectionTitle>Reschedule</SectionTitle>
              <SectionText>{rescheduleDescription}</SectionText>
              <DateInputLabel htmlFor="payment-plan-instalment-reschedule-date">Reschedule date</DateInputLabel>
              <DateChangeRow>
                <CurrentDateValue>{formatDateWithSlashes(instalment.dueDate)}</CurrentDateValue>
                <DateChangeArrow>→</DateChangeArrow>
                <DateInputWrapper>
                  <DateInput
                    disabled={isFormDisabled}
                    id="payment-plan-instalment-reschedule-date"
                    min={today}
                    onChange={(event) => {
                      setRescheduledDate(event.target.value);
                      setHasSubmittedDate(false);
                    }}
                    type="date"
                    value={rescheduledDate}
                  />
                </DateInputWrapper>
              </DateChangeRow>
              {validationError ? <ValidationError>{validationError}</ValidationError> : null}

              <ActionBar>
                <DisabledActionTooltip title={previewDisabledReason}>
                  <Button onClick={handlePreviewReschedule} disabled={isPreviewButtonDisabled}>
                    Preview reschedule
                  </Button>
                </DisabledActionTooltip>
              </ActionBar>
            </>
          ) : null}

          {canDeleteInstalment ? (
            <>
              <Divider />

              <SectionTitle>Delete instalment</SectionTitle>
              <SectionText>
                Remove the {instalment.status.toLowerCase()} instalment and reduce the payment plan total.
              </SectionText>
              <ActionBar>
                <Button
                  buttonType="warning"
                  onClick={() => setIsDeleteConfirmationVisible(true)}
                  disabled={isFormDisabled}
                  noArrow
                >
                  Delete Instalment
                </Button>
              </ActionBar>
            </>
          ) : null}
        </ScrollableFormContent>

        <FormFooter>
          <Divider />

          <LeftAlignedAction>
            <Button buttonType="secondary" onClick={handleDismiss} disabled={isFormDisabled} noArrow>
              Go back
            </Button>
          </LeftAlignedAction>
        </FormFooter>
      </FixedModalLayout>
    </Modal>
  );
};

export default ModifyPaymentPlanInstalmentModal;
